/**
 * Utility functions for authentication and token management
 */

/**
 * Decode JWT token to get payload
 */
const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Check if token is expired
 * Returns true if token is expired or invalid
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return true;
  }

  try {
    const decoded = decodeToken(token);
    
    // If token has exp (expiration) claim, check it
    if (decoded && decoded.exp) {
      const expirationTime = decoded.exp * 1000; // Convert to milliseconds
      return Date.now() >= expirationTime;
    }

    // If no exp claim, check if token was stored more than 24 hours ago
    const tokenTimestamp = localStorage.getItem("token_timestamp");
    if (tokenTimestamp) {
      const tokenTime = parseInt(tokenTimestamp, 10);
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      return Date.now() - tokenTime >= twentyFourHours;
    }

    // If no timestamp stored, assume expired (shouldn't happen in normal flow)
    return true;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
};

/**
 * Store token and timestamp
 */
export const storeToken = (token: string): void => {
  localStorage.setItem("token", token);
  localStorage.setItem("token_timestamp", Date.now().toString());
};

/**
 * Clear token and related data
 */
export const clearToken = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("token_timestamp");
  localStorage.removeItem("isAuth");
};

/**
 * Get token if valid, otherwise return null
 */
export const getValidToken = (): string | null => {
  const token = localStorage.getItem("token");
  if (token && !isTokenExpired(token)) {
    return token;
  }
  return null;
};

